import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { trackCallUpdateThunk } from "./Slices/salesSlice";
import { timerHistoryPayloadChange } from "./Slices/user";
import { Starttask } from "./Slices/timerslice";
import { updateWorkHistoryThunk } from "./Slices/orderSlice";

const RingCentralContext = React.createContext();

export const RingCentralProvider = ({ children }) => {
  const WorkingId = useSelector((state) => state?.timer?.requestId);
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState(null);

  const [lastcalltime, setlastcalltime] = useState(null);
  const lastCallTimeRef = useRef(null);
  const dispatch = useDispatch();

  const receivelastcalltime = (data) => setlastcalltime(data);
  useEffect(() => { lastCallTimeRef.current = lastcalltime; }, [lastcalltime]);

  // Read token from URL/localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get("token");

    if (tokenFromUrl) {
      localStorage.setItem("rc_access_token", tokenFromUrl);
      setAccessToken(tokenFromUrl);
      setIsAuthenticated(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      const storedToken = localStorage.getItem("rc_access_token");
      if (storedToken) {
        setAccessToken(storedToken);
        setIsAuthenticated(true);
      }
    }
  }, []);

  // Load RingCentral Adapter (with Noise Reduction support) and init
  useEffect(() => {
    if (!(isAuthenticated && accessToken)) return;

    // ✅ Correct manual init mode for adapter.js
    window.RC_EMBEDDABLE_ADAPTER_MANUAL_INIT = true;

    if (!document.getElementById("rc-adapter-script")) {
      const script = document.createElement("script");
      script.id = "rc-adapter-script";

      // ✅ Must load from apps.ringcentral.com; NR flag helps on 1.x and is harmless on 2.x
      script.src =
        "https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/adapter.js?enableNoiseReductionSetting=1";

      script.onload = () => {
        // Initialize adapter (manual)
        if (window.RCAdapterInit) {
          window.RCAdapterInit();
        }

        // Supply auth
        if (window.rcAdapter) {
          window.rcAdapter.setAuthData({
            tokenType: "Bearer",
            accessToken,
            expiresIn: 3600,
          });
        }

        // Minimize on load
        setTimeout(() => {
          const iframe = document.querySelector("#rc-widget-adapter-frame");
          iframe?.contentWindow?.postMessage({ type: "rc-adapter-minimize" }, "*");
        }, 1000);
      };

      document.body.appendChild(script);
    }
  }, [isAuthenticated, accessToken]);

  // 🔹 Global Call Event Listener
  useEffect(() => {
    let sessionData = null;

    function handleWidgetMessage(event) {
      if (!event?.data?.type) return;

      if (event.data.type === "rc-telephony-session-notify") {
        sessionData = event.data;
        return;
      }

      if (
        event.data.type === "rc-call-end-notify" ||
        event.data.type === "rc-adapter-call-end"
      ) {
        if (!sessionData) {
          console.warn("⚠️ Call end event received but no session data available.");
          return;
        }
        if (!loginUser?.id) {
          console.error("Login user ID not available at call end");
          return;
        }

        const sessionId = sessionData.telephonySession?.sessionId;

        const valuesObject = {
          id: Math.random().toString(),
          userId: loginUser.id,
          title: "",
          description: "",
          clientName: "Blueplanit",
          department: "Sales",
          taskType: "Available",
          startTime: Date.now().toString(),
          endTime: Date.now().toString(),
          requestId: "",
        };

        dispatch(timerHistoryPayloadChange({ data: valuesObject }));
        dispatch(updateWorkHistoryThunk(valuesObject));
        dispatch(Starttask({ department: "", task: "Available", requestId: "", time: Date.now() }));
        dispatch(
          trackCallUpdateThunk({
            sessionId,
            leadID: WorkingId,
            lastcalltime: lastCallTimeRef.current,
          }, { dispatch })
        );
      }
    }

    window.addEventListener("message", handleWidgetMessage);
    return () => window.removeEventListener("message", handleWidgetMessage);
  }, [dispatch, loginUser, WorkingId]);

  return (
    <RingCentralContext.Provider value={{ isAuthenticated, accessToken, receivelastcalltime }}>
      {children}
      {/* Mount rc-widget directly on document.body via portal so the RC SDK iframe
          is outside React's stacking context — fixes z-index issues with side panels */}
      {ReactDOM.createPortal(<div id="rc-widget" />, document.body)}
    </RingCentralContext.Provider>
  );
};

export default RingCentralContext;
